/* One definition of how a chart looks, shared by the editor preview, the dashboard
   and the report HTML view. These three drew the same widget from three separate
   copies of the ApexCharts options, so the editor's restyle left the view pages on
   the old default look. */

/* Slice/series palette. First entry is also the single-series colour for bar/line.

   ── THE FIRST TWO ARE THE COMPARISON PAIR ──
   A two-period chart draws one series per period, so entries 0 and 1 are no longer
   just "the first two slices" - they are Period 1 and Period 2 on every line and bar
   chart in the app, and the pair has to read as a pair at a glance.

   The navy is unchanged. The amber that used to sit in slot 1 is now #2dd4bf: a cool
   teal against the cool navy, which separates the two periods by lightness rather than
   by temperature and keeps the chart in one family.

   #4ac9a4 followed it out of slot 2 - it is a green-teal within a few percent of
   #2dd4bf, so a donut with three categories would have drawn slices 2 and 3 in what
   looks like the same colour. #a78bfa takes that place: distinct from both, and light
   enough not to be mistaken for the navy. */
export const CHART_COLORS = ["#1a2e5a", "#2dd4bf", "#a78bfa", "#c0c8d8", "#e05c5c", "#6c63ff", "#34a853"];

const AXIS_LABEL_COLOR = "#6b7a99";

/* Bar and line. A line is drawn as a filled area — the gradient is what makes it read
   as the new design rather than a bare stroke. */
export const buildCartesianChartOptions = ({ chartType, categories, xTitle, yTitle }) => {
  const isLine = String(chartType).toLowerCase() === "line";

  return {
    chart: {
      type: isLine ? "area" : String(chartType).toLowerCase(),
      toolbar: { show: false },
      zoom: { enabled: false },
      background: "transparent",
      fontFamily: "inherit",
    },
    /* The widget header already shows the name, so drawing it inside the chart
       repeated it and ate vertical space. */
    /* The WHOLE palette, not just the first colour. A comparison draws one series
       per period, and with a single-entry array ApexCharts reuses it - so both
       periods came out the same navy and the second line was invisible against the
       first. A one-series chart is unaffected: it still takes CHART_COLORS[0]. */
    colors: CHART_COLORS,
    /* Only meaningful with more than one series, and ApexCharts hides it by itself
       when there is one - but set explicitly rather than left to a library default,
       since it is now the only thing naming which line is which period. */
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      labels: { colors: "#1a2e5a" },
      markers: { width: 10, height: 10, radius: 3 },
      itemMargin: { horizontal: 10 },
    },

    fill: isLine
      ? {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.25,
            opacityTo: 0.02,
            stops: [0, 90, 100],
          },
        }
      : { type: "solid" },

    stroke: isLine ? { curve: "smooth", width: 2 } : { show: false },

    ...(!isLine && {
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "50%",
        },
      },
    }),

    dataLabels: { enabled: false },

    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 16, bottom: 0, left: 8 },
    },

    xaxis: {
      categories: categories || [],
      title: {
        text: xTitle,
        style: { fontSize: "11px", fontWeight: 500, color: AXIS_LABEL_COLOR },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { fontSize: "11px", colors: AXIS_LABEL_COLOR },
        rotate: -45,
        trim: true,
        maxHeight: 80,
      },
    },

    yaxis: {
      title: {
        text: yTitle,
        style: { fontSize: "11px", fontWeight: 500, color: AXIS_LABEL_COLOR },
      },
      labels: {
        style: { fontSize: "11px", colors: AXIS_LABEL_COLOR },
      },
    },

    tooltip: {
      theme: "light",
      y: { formatter: (val) => `${val}` },
    },
  };
};

/* Pie. Drawn as a donut with the legend on the right — a plain pie is the old look. */
export const buildDonutChartOptions = ({ labels }) => ({
  chart: {
    type: "donut",
    toolbar: { show: false },
    background: "transparent",
    fontFamily: "inherit",
  },
  labels: labels || [],
  colors: CHART_COLORS,
  legend: {
    position: "right",
    fontSize: "12px",
    fontFamily: "inherit",
    labels: {
      colors: "#1a2e5a",
    },
    markers: {
      width: 10,
      height: 10,
      radius: 50,
    },
    itemMargin: {
      vertical: 4,
    },
    onItemClick: {
      toggleDataSeries: false, // ← disable slice toggle on legend click
    },
    onItemHover: {
      highlightDataSeries: false, // ← disable highlight on hover (also reduces re-renders)
    },
  },
  plotOptions: {
    pie: {
      donut: {
        size: "65%",
        labels: {
          show: true,
          name: {
            show: true,
            label: "Total",
            formatter: () => "Total",
            color: AXIS_LABEL_COLOR,
            fontSize: "13px",
          },
          value: {
            show: true,
            fontSize: "22px",
            fontWeight: 700,
            color: "#1a2e5a",
            formatter: () => "100%",
          },
          total: {
            show: true,
            label: "Total",
            color: AXIS_LABEL_COLOR,
            fontSize: "13px",
            formatter: () => "100%",
          },
        },
      },
    },
  },
  dataLabels: {
    enabled: false, // no percentage labels on slices
  },
  stroke: {
    width: 2,
    colors: ["#fff"], // white gap between slices
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        legend: { position: "bottom" },
      },
    },
  ],
  tooltip: {
    y: {
      formatter: (val) => `${val}`,
    },
  },
});
